import type { State, OrganizationState } from '../types';
import type { SearchResult } from '../../layout/parts/search-result';
import { entities, occurrenceDateSuffix } from '../../models/search';
import { searchIndex, isIndexReady, rebuildSearchIndex } from '../services/search-index';
import type { SearchIndexResult } from '../services/search-index-types';
import { employeeNamesById as selectEmployeeNamesById } from './employees/employee-names-by-id';
import { partnerNamesById as selectPartnerNamesById } from './partners/partner-names-by-id';

function pathToHref(organizationId: number, path: string): string {
  return `/account/${organizationId}/${path}`;
}

function hitToSearchResult(
  hit: SearchIndexResult,
  organizationId: number,
  organization: OrganizationState,
): SearchResult {
  const path = computeResultPath(hit.entity, hit.uuid, organization);
  return {
    id: hit.uuid,
    entity: hit.entity,
    description: hit.name + occurrenceDateSuffix(hit.entity, hit.uuid, organization),
    party: hit.party || '',
    href: pathToHref(organizationId, path),
    target: '_self',
  };
}

/**
 * Computes the result path for an entity
 * This mirrors the logic from models/search.ts resultUrl()
 */
function computeResultPath(entity: string, uuid: string, o: OrganizationState): string {
  if (entity === 'Partner') {
    return '104/partners/' + uuid;
  }
  if (entity === 'Employee') {
    return '104/employees/' + uuid;
  }
  if (entity === 'Computer') {
    return '20';
  }
  if (entity === 'Network') {
    return '20';
  }
  if (entity === 'PersonalDataItem') {
    return '20';
  }
  if (entity === 'MeetingOccurrence') {
    return '9772/meetingOccurrences/' + uuid;
  }
  if (entity === 'EventOccurrence') {
    return '318434/eventOccurrences/' + uuid;
  }
  if (entity === 'Asset') {
    return '110/assets/' + uuid;
  }
  if (entity === 'Issue') {
    return '882901/issues/' + uuid;
  }
  if (entity === 'RiskAssessment') {
    return '712363/riskAssessments/' + uuid;
  }
  if (entity === 'Substance') {
    return '12368/substances/' + uuid;
  }
  if (entity === 'ExternalConnection') {
    return '20';
  }
  if (entity === 'Task' || entity === 'StartTask') {
    const task = o.tasksById[uuid];
    if (task !== undefined) {
      const parent = o.functionsById[task.functionUuid];
      if (parent === undefined) {
        return '357/tasks/' + uuid;
      }
      const responsible = o.functionsById[task.responsibleFunctionUuid ?? ''];
      if (!o.singleUser) {
        return parent.pages[0] + '/functions/' + parent.uuid + '/tasks/' + uuid;
      } else if (task.pages && task.pages.length > 0) {
        return task.pages[0] + '/tasks/' + uuid;
      } else if (parent.type !== 'COMMON') {
        return parent.pages[0] + '/tasks/' + uuid;
      } else if (responsible) {
        return responsible.pages[0] + '/tasks/' + uuid;
      } else {
        return '357/tasks/' + uuid;
      }
    }
    return '357/tasks/' + uuid;
  }

  const map = {
    Contract: o.contractsById,
    Document: o.documentsById,
    Guideline: o.guidelinesById,
    Function: o.functionsById,
    Report: o.reportsById,
    ConstitutionalDocument: o.constitutionalDocumentsById,
  };

  const items = map[entity];
  if (!items) {
    return '695944';
  }

  const item = items[uuid];
  if (item && item.pages && item.pages.length) {
    return '' + item.pages[0] + '/' + entities[entity] + '/' + uuid;
  } else {
    return '695944/' + entities[entity] + '/' + uuid;
  }
}

/**
 * Search using MiniSearch index with relevance ranking.
 * Falls back to synchronous index rebuild if index is not ready.
 */
export function selectLocalSearchResults(state: State, query: string, limit = 200): SearchResult[] {
  const organization = state.organization;
  const organizationId = state.id;

  if (!organization || !organizationId || query.length < 3) {
    return [];
  }

  if (!isIndexReady()) {
    rebuildSearchIndex(
      organization,
      organization.singleUser,
      selectEmployeeNamesById(state),
      selectPartnerNamesById(state),
    );
  }

  const hits = searchIndex(query, limit);
  return hits.map((hit) => hitToSearchResult(hit, organizationId, organization));
}
